import { useEffect, useCallback, useRef, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { getTokenIntel, getIntelAccessToken, getDeepTokenIntel } from "../services/api.js";
import { AlertTriangleIcon, ShieldCheckIcon } from "../components/Icons.js";
import { formatSafetyScore } from "../lib/scoreFormatter.js";
import {
  SafetySummaryPanel,
  WalletIntelPanel,
  FundingAttributionPanel,
  ContractIntelPanel,
  ApprovalIntelPanel,
  LiquidityIntelPanel,
  LockerIntelPanel,
  HolderIntelPanel,
  SocialIntelPanel,
  ConfidencePanel,
  CollapsibleSection,
} from "../components/intel/index.js";
import { LEGAL_DISCLAIMER } from "../data/metric-explanations.js";
import { normalizeIntel } from "../utils/intelNormalizer.js";

/* ------------------------------------------------------------------ */
/* Types — reuse from TokenIntel                                       */
/* ------------------------------------------------------------------ */

type RiskLevel = "GREEN" | "YELLOW" | "RED" | "UNKNOWN";

interface SnapIntelResponse {
  ok: boolean;
  tier: "preview" | "deep";
  data: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */

export function SnapDeepIntelPage() {
  const { chainId, contract } = useParams<{ chainId: string; contract: string }>();
  const { isAuthenticated, token: sessionToken, walletAddress } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewResp, setPreviewResp] = useState<SnapIntelResponse | null>(null);
  const [deepResp, setDeepResp] = useState<SnapIntelResponse | null>(null);
  const deepFetchedRef = useRef(false);

  /* Step 1: Fetch preview (public — no auth required) */
  useEffect(() => {
    if (!chainId || !contract) return;
    setLoading(true);
    setError(null);
    setPreviewResp(null);
    setDeepResp(null);
    deepFetchedRef.current = false;

    getTokenIntel(chainId, contract)
      .then((data: SnapIntelResponse) => {
        setPreviewResp(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message || "Failed to load intelligence");
        setLoading(false);
      });
  }, [chainId, contract]);

  /* Step 2: Fetch deep ONLY if authenticated (upgrade path) */
  const fetchDeep = useCallback(async () => {
    if (!chainId || !contract || !sessionToken || !isAuthenticated) return;
    if (deepFetchedRef.current) return;
    deepFetchedRef.current = true;

    try {
      const access = await getIntelAccessToken(sessionToken);
      if (access.tier === "pro") {
        const deep = await getDeepTokenIntel(chainId, contract, access.token, walletAddress ?? undefined);
        setDeepResp(deep);
      }
    } catch {
      // Non-critical — show preview if deep fails
    }
  }, [chainId, contract, sessionToken, isAuthenticated, walletAddress]);

  useEffect(() => {
    if (previewResp && isAuthenticated && sessionToken) {
      fetchDeep();
    }
  }, [previewResp, isAuthenticated, sessionToken, fetchDeep]);

  const resp = deepResp ?? previewResp;
  const d = resp?.data as Record<string, any> | null;
  const tier = resp?.tier ?? "preview";
  const deep = tier === "deep" ? d : null;
  const intel = deep ? normalizeIntel(deep) : null;

  const riskLevel: RiskLevel = (d?.riskLevel as RiskLevel) ?? "UNKNOWN";
  const score = (d?.score as number) ?? 0;
  const tokenName = d?.project?.name ?? d?.project?.ticker ?? "Token";

  const RISK_COLORS: Record<RiskLevel, string> = {
    GREEN: "#27ae60",
    YELLOW: "#f39c12",
    RED: "#e74c3c",
    UNKNOWN: "#95a5a6",
  };

  useEffect(() => {
    document.title = `${tokenName} — CryptoGuardians Snap Intel`;
  }, [tokenName]);

  /* Derive top reasons from risks + signals */
  const topReasons = useMemo(() => {
    if (!intel) return [];
    const reasons: string[] = [];
    for (const r of intel.risks) { if (reasons.length < 3) reasons.push(r); }
    for (const p of intel.positives) { if (reasons.length < 3) reasons.push(p); }
    return reasons;
  }, [intel]);

  /* ------------------------------------------------------------------ */
  /* ALWAYS render the main page layout — never return null for auth     */
  /* ------------------------------------------------------------------ */

  return (
    <div className="snap-intel-page">
      {/* Header — always visible */}
      <div className="snap-intel-header">
        <h1 className="snap-intel-title">
          <ShieldCheckIcon style={{ width: 20, height: 20, marginRight: 6, verticalAlign: 'middle' }} />
          {loading ? "Analyzing..." : tokenName}
        </h1>
        <span className="snap-chain-badge">{chainId?.toUpperCase() ?? "—"}</span>
      </div>

      {/* Wallet connection banner — read-only intel still shown below */}
      {!isAuthenticated && (
        <div className="snap-connect-banner">
          Connect wallet to enable active protection
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="snap-intel-loading">
          <div className="snap-spinner" />
          <p>Analyzing token...</p>
        </div>
      )}

      {/* Error state — shown inline, not as a full-page block */}
      {!loading && (error || !d) && (
        <div className="snap-intel-error">
          <AlertTriangleIcon className="snap-error-icon" />
          <h2>Analysis Unavailable</h2>
          <p>{error || "Could not load intelligence data."}</p>
        </div>
      )}

      {/* Main intel content — renders whenever data is available */}
      {!loading && d && (
        <>
          {/* SECTION 1: Safety Summary — always visible */}
          <SafetySummaryPanel
            data={{
              score: formatSafetyScore(score) ?? 0,
              riskLevel,
              confidence: intel?.analysisConfidence || undefined,
              confidenceLevel: intel?.analysisConfidenceLevel || undefined,
              summary: intel?.verdictSummary || undefined,
            }}
          />

          {/* Top Reasons */}
          {topReasons.length > 0 && (
            <div className="snap-reasons">
              <h3 className="snap-reasons-title">Key Findings</h3>
              <ul className="snap-reasons-list">
                {topReasons.map((r, i) => (
                  <li key={i} className="snap-reason-item">
                    <span className="snap-reason-bullet" style={{ color: RISK_COLORS[riskLevel] }}>●</span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Upgrade hint for non-deep users */}
          {!intel && (
            <div className="snap-upgrade-hint">
              <p>Connect your wallet and upgrade to Pro for detailed intelligence.</p>
            </div>
          )}

          {/* Detailed sections — collapsed by default */}
          {intel && (
            <div className="snap-details">
              <CollapsibleSection title="Creator & Wallet Intelligence">
                <WalletIntelPanel data={intel.creatorRisk} />
                <FundingAttributionPanel data={intel.creatorFunding} />
              </CollapsibleSection>

              <CollapsibleSection title="Contract Risk Assessment">
                <ContractIntelPanel data={intel.contractSafety} />
                <ApprovalIntelPanel data={intel.contractApproval} />
              </CollapsibleSection>

              <CollapsibleSection title="Liquidity & Exit Risk">
                <LiquidityIntelPanel data={intel.liquiditySafety} />
                <LockerIntelPanel data={intel.liquidityLocker} />
              </CollapsibleSection>

              <CollapsibleSection title="Holder Distribution">
                <HolderIntelPanel data={intel.holderRisk} />
              </CollapsibleSection>

              <CollapsibleSection title="Social & Reputation">
                <SocialIntelPanel data={intel.socialTrust} />
              </CollapsibleSection>

              <CollapsibleSection title="Analysis Reliability">
                <ConfidencePanel data={intel.eliteThreat as any} />
              </CollapsibleSection>
            </div>
          )}
        </>
      )}

      {/* Legal — always visible */}
      <p className="snap-disclaimer">{LEGAL_DISCLAIMER}</p>
    </div>
  );
}
